import useLocalStorageState from "use-local-storage-state";
import useCurrentUser from "./useCurrentUser";
import { c } from "framer-motion/dist/types.d-6pKw1mTI";
import { useTransition } from "react";

const COLLAPSIBLES_KEY = "collapsibles";

export enum EReportsCollapsibleKeys {
  LATEST_TRANSACTIONS_TABLE = "latest-transactions-table",
  LATEST_TRANSACTIONS_CHART = "latest-transactions-chart",
  MONTHLY_TRANSACTIONS_TABLE = "monthly-transactions-table",
  MONTHLY_TRANSACTIONS_CHART = "monthly-transactions-chart",
  CATEGORY_TRANSACTIONS_TABLE = "category-transactions-table",
  CATEGORY_TRANSACTIONS_CHART = "category-transactions-chart",
  CLIENT_PROVIDER_TRANSACTIONS_TABLE = "client-provider-transactions-table",
  CLIENT_PROVIDER_TRANSACTIONS_CHART = "client-provider-transactions-chart",
  PAYMENT_METHOD_TRANSACTIONS_TABLE = "payment-methods-table",
  PAYMENT_METHOD_TRANSACTIONS_CHART = "payment-methods-chart",
  STATUS_TRANSACTIONS_TABLE = "status-transactions-table",
  STATUS_TRANSACTIONS_CHART = "status-transactions-chart",
}

export enum ENotificationsCollapsibleKeys {
  OVERDUE_TRANSACTIONS_COLLAPSIBLE = "overdue-transactions-collapsible",
  UPCOMING_TRANSACTIONS_COLLAPSIBLE = "upcoming-transactions-collapsible",
  TODAY_TRANSACTIONS_COLLAPSIBLE = "today-transactions-collapsible",
}

export enum ERecurringTransactionsCollapsibleKeys {
  OVERDUE_TRANSACTIONS_COLLAPSIBLE = "overdue-transactions-collapsible",
  UPCOMING_TRANSACTIONS_COLLAPSIBLE = "upcoming-transactions-collapsible",
  TODAY_TRANSACTIONS_COLLAPSIBLE = "today-transactions-collapsible",
  ALL_TRANSACTIONS_TABLE = "all-transactions-table",
}

export enum EAttachmentsCollapsibleKeys {
  FILE_TYPES_COLLAPSIBLE = "file-types-collapsible",
  ATTACHMENTS_TABLE = "attachments-table",
}

export enum ETransactionPageMobileCollapsibles {
  HEADER = "transaction-header",
  NOTES = "transaction-notes",
  DETAILS = "transaction-details",
  ATTACHMENTS = "transaction-attachments",
  TOTALS = "transaction-totals",
  PAYMENT = "transaction-payment",
}

export enum ESummaryPageMobileCollapsibles {
  GENERAL = "summary-general",
  PENDING = "summary-pending",
  TRANSACTIONS = "summary-transactions",
  ATTACHMENTS = "summary-attachments",
  CONTACT_REQUESTS = "summary-contact-requests",
}

export enum ESourcesPageMobileCollapsibles {
  ICON_COLORS = "icon-and-colors",
  CONTACT_DATA = "contact-data",
}

export enum ESearchMobileCollapsibles {
  DATES = "dates",
  COMMON = "common-fields",
}

export enum EMoneyTransferMobileCollapsibles {
  ATTACHMENTS = "money-transfer-attachments",
  DATA = "money-transfer-data",
}

export enum EStatsMobileCollapsibles {
  TABLE = "table",
  CHART = "chart",
}

export enum ERecurringTransactionMobileCollapsibles {
  ATTACHMENTS = "recurring-transaction-attachments",
  DETAILS = "recurring-transaction-details",
}

export enum ERecurringTransactionsMobileCollapsibles {
  OVERDUE = "recurring-transactions-overdue",
  DUE_SOON = "recurring-transactions-due-soon",
  TODAY = "recurring-transactions-today",
  ALL = "recurring-transactions-all",
}

export enum EAttachmentsPageMobileCollapsibles {
  TRANSACTION = "attachments-transaction",
  FILE_INFO = "attachments-file-info",
  TABLE = "table",
  CHART = "chart",
}

export enum EAppearancePageMobileCollapsibles {
  THEME = "appearance-theme",
  TRANSACTIONS = "appearance-transactions",
  BALANCE = "appearance-balance",
  MONTHS = "appearance-months",
  DAYS = "appearance-days",
}

export enum ETablePreferencesMobileCollapsibles {
  PAGINATION = "table-pagination",
  COLUMNS = "table-columns",
  SELECTION = "table-selection",
}

export enum EExportTransactionsMobileCollapsibles {
  WHAT_TO_EXPORT = "what-to-export",
  ADDITIONAL_OPTIONS = "additional-options",
  FILE_INFO = "file-info",
}

export enum EPaymentMethodsMobileCollapsibles {
  BUILTIN = "builtin",
  CUSTOM = "custom",
}

export enum EContactsPageMobileCollapsibles {
  ACCEPTED = "accepted",
  RECEIVED = "received",
  SENT = "sent",
}

const DEFAULTS = {
  reports: {
    [EReportsCollapsibleKeys.LATEST_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.MONTHLY_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.CATEGORY_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.CLIENT_PROVIDER_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.PAYMENT_METHOD_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.STATUS_TRANSACTIONS_TABLE]: true,
    [EReportsCollapsibleKeys.LATEST_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.MONTHLY_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.CATEGORY_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.CLIENT_PROVIDER_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.PAYMENT_METHOD_TRANSACTIONS_CHART]: true,
    [EReportsCollapsibleKeys.STATUS_TRANSACTIONS_CHART]: true,
  },
  notifications: {
    [ENotificationsCollapsibleKeys.TODAY_TRANSACTIONS_COLLAPSIBLE]: true,
    [ENotificationsCollapsibleKeys.OVERDUE_TRANSACTIONS_COLLAPSIBLE]: true,
    [ENotificationsCollapsibleKeys.UPCOMING_TRANSACTIONS_COLLAPSIBLE]: true,
  },
  recurring: {
    [ERecurringTransactionsCollapsibleKeys.TODAY_TRANSACTIONS_COLLAPSIBLE]:
      true,
    [ERecurringTransactionsCollapsibleKeys.OVERDUE_TRANSACTIONS_COLLAPSIBLE]:
      true,
    [ERecurringTransactionsCollapsibleKeys.UPCOMING_TRANSACTIONS_COLLAPSIBLE]:
      true,
    [ERecurringTransactionsCollapsibleKeys.ALL_TRANSACTIONS_TABLE]: true,
  },
  attachments: {
    [EAttachmentsCollapsibleKeys.ATTACHMENTS_TABLE]: true,
    [EAttachmentsCollapsibleKeys.FILE_TYPES_COLLAPSIBLE]: true,
  },
  shared: {
    accountOpen: true,
  },

  accountOpen: true,
  summaryOpen: false,
  generalAdjustmentsOpen: false,
  selectionOpen: true,
  statsTableOpen: true,
  statsChartOpen: true,
  statsMobileOpenedCollapsible: "",

  transactionHeaderOpen: true,
  transactionNotesOpen: true,
  transactionAttachmentsOpen: true,
  transactionDetailsOpen: true,
  transactionAmountsOpen: true,
  transactionPaymentOpen: true,
  fileInfoOpen: true,
  transactionInfoOpen: true,
  searchDatesOpen: false,
  searchCommonFieldsOpen: true,
  searchMobileOpenedCollapsible: "",
  navIsCollapsed: true,
  pendingToPayOpen: true,
  pendingToCollectOpen: true,
  receivedInvitesOpen: true,
  transactionsMobileOpenedCollapsible: "",
  moneyTransferDataOpen: true,
  moneyTransferAttachmentsOpen: true,
  moneyTransferMobileOpenedCollapsible: "",
  summaryGeneralOpen: true,
  summaryTransactionsOpen: true,
  summaryPendingOpen: true,
  summaryContactRequestsOpen: true,
  summaryAttachmentsOpen: true,
  summaryMobileOpenedCollapsible: "",
  attachmentsInfoMobileOpenedCollapsible: "",
  lightboxThumbnailsOpen: true,
  attachmentsTableOpen: true,
  attachmentsChartOpen: true,

  //
  appearanceThemeOpen: true,
  appearanceTransactionsOpen: true,
  appearanceBalanceOpen: true,
  appearanceMonthsOpen: true,
  appearanceDaysOpen: true,
  apperanceMobileOpenedCollapsible: "",

  //
  tablePreferencesPaginationOpen: true,
  tablePreferencesColumnsOpen: true,
  tablePreferencesSelectionOpen: true,
  tablePreferencesMobileOpenedCollapsible: "",

  //
  recurringTransactionAttachmentsOpen: true,
  recurringTransactionDetailsOpen: true,
  recurringTransactionMobileOpenedCollapsible: "",

  //
  recurringTransactionsOverdueOpen: true,
  recurringTransactionsDueSoonOpen: true,
  recurringTransactionsTodayOpen: true,
  recurringTransactionsAllOpen: true,
  recurringTransactionsMobileOpenedCollapsible: "",

  //
  sourcesIconAndColorsOpen: true,
  sourcesContactDataOpen: true,
  sourcesMobileOpenedCollapsible: "",

  //
  exportTransactionsWhatToExportOpen: true,
  exportTransactionsAdditionalOptionsOpen: true,
  exportTransactionsFileInfoOpen: true,
  exportTransactionsMobileOpenedCollapsible: "",

  //
  paymentMethodsBuiltInOpen: true,
  paymentMethodsCustomOpen: true,
  paymentMethodsMobileOpenedCollapsible: "",

  // contacts
  contactsPageAcceptedOpen: true,
  contactsPageReceivedOpen: true,
  contactsPageSentOpen: true,
  contactsPageMobileOpenedCollapsible: "",
};

export default function useCollapsibles() {
  const [isPending, startTransition] = useTransition();
  const { userInfo } = useCurrentUser();
  const [collapsiblePreferences, setCollapsiblePreferences] =
    useLocalStorageState(`${userInfo?._id}-${COLLAPSIBLES_KEY}`, {
      defaultValue: DEFAULTS,
    });

  return {
    collapsiblePreferences,
    updateCollapsible: (newState: Partial<typeof DEFAULTS>) => {
      startTransition(() =>
        setCollapsiblePreferences((prev: any) => ({ ...prev, ...newState }))
      );
    },
    isTransactionPageMobileCollapsibleOpen: (
      collapsible: ETransactionPageMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.transactionsMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleTransactionPageMobileCollapsible: (
      collapsible: ETransactionPageMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.transactionsMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            transactionsMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            transactionsMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isAttachmentsPageMobileCollapsibleOpen: (
      collapsible: EAttachmentsPageMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.attachmentsInfoMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleAttachmentsPageMobileCollapsible: (
      collapsible: EAttachmentsPageMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.attachmentsInfoMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            attachmentsInfoMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            attachmentsInfoMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isAppearancePageMobileCollapsibleOpen: (
      collapsible: EAppearancePageMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.apperanceMobileOpenedCollapsible === collapsible
      );
    },
    toggleAppearancePageMobileCollapsible: (
      collapsible: EAppearancePageMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.apperanceMobileOpenedCollapsible === collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            apperanceMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            apperanceMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isTablePreferencesMobileCollapsibleOpen: (
      collapsible: ETablePreferencesMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.tablePreferencesMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleTablePreferencesMobileCollapsible: (
      collapsible: ETablePreferencesMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.tablePreferencesMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            tablePreferencesMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            tablePreferencesMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isSummaryPageMobileCollapsibleOpen: (
      collapsible: ESummaryPageMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.summaryMobileOpenedCollapsible === collapsible
      );
    },
    toggleSummaryPageMobileCollapsible: (
      collapsible: ESummaryPageMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.summaryMobileOpenedCollapsible === collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            summaryMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            summaryMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isMoneyTransferMobileCollapsibleOpen: (
      collapsible: EMoneyTransferMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.moneyTransferMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleMoneyTransferMobileCollapsible: (
      collapsible: EMoneyTransferMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.moneyTransferMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            moneyTransferMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            moneyTransferMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isRecurringTransactionMobileCollapsibleOpen: (
      collapsible: ERecurringTransactionMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.recurringTransactionMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleRecurringTransactionMobileCollapsible: (
      collapsible: ERecurringTransactionMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.recurringTransactionMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            recurringTransactionMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            recurringTransactionMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isRecurringTransactionsMobileCollapsibleOpen: (
      collapsible: ERecurringTransactionsMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.recurringTransactionsMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleRecurringTransactionsMobileCollapsible: (
      collapsible: ERecurringTransactionsMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.recurringTransactionsMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            recurringTransactionsMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            recurringTransactionsMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isStatsMobileCollapsibleOpen: (collapsible: EStatsMobileCollapsibles) => {
      return (
        collapsiblePreferences.statsMobileOpenedCollapsible === collapsible
      );
    },
    toggleStatsMobileCollapsible: (collapsible: EStatsMobileCollapsibles) => {
      if (collapsiblePreferences.statsMobileOpenedCollapsible === collapsible) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            statsMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            statsMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
    isSourceMobileCollapsibleOpen: (
      collapsible: ESourcesPageMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.sourcesMobileOpenedCollapsible === collapsible
      );
    },
    toggleSourcesMobileCollapsible: (
      collapsible: ESourcesPageMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.sourcesMobileOpenedCollapsible === collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            sourcesMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            sourcesMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },

    isExportTransactionsMobileCollapsibleOpen: (
      collapsible: EExportTransactionsMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.exportTransactionsMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleExportTransactionsMobileCollapsible: (
      collapsible: EExportTransactionsMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.exportTransactionsMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            exportTransactionsMobileOpenedCollapsible: "",
          }))
        );
      } else {
        setCollapsiblePreferences((prev: any) => ({
          ...prev,
          exportTransactionsMobileOpenedCollapsible: collapsible,
        }));
      }
    },

    isSearchMobileCollapsibleOpen: (collapsible: ESearchMobileCollapsibles) => {
      return (
        collapsiblePreferences.searchMobileOpenedCollapsible === collapsible
      );
    },
    toggleSearchMobileCollapsible: (collapsible: ESearchMobileCollapsibles) => {
      if (
        collapsiblePreferences.searchMobileOpenedCollapsible === collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            searchMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            searchMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },

    isPaymentMethodsMobileCollapsibleOpen: (
      collapsible: EPaymentMethodsMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.paymentMethodsMobileOpenedCollapsible ===
        collapsible
      );
    },
    togglePaymentMethodsMobileCollapsible: (
      collapsible: EPaymentMethodsMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.paymentMethodsMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            paymentMethodsMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            paymentMethodsMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },

    isContactsPagesMobileCollapsibleOpen: (
      collapsible: EContactsPageMobileCollapsibles
    ) => {
      return (
        collapsiblePreferences.contactsPageMobileOpenedCollapsible ===
        collapsible
      );
    },
    toggleContactsPageMobileCollapsible: (
      collapsible: EContactsPageMobileCollapsibles
    ) => {
      if (
        collapsiblePreferences.contactsPageMobileOpenedCollapsible ===
        collapsible
      ) {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            contactsPageMobileOpenedCollapsible: "",
          }))
        );
      } else {
        startTransition(() =>
          setCollapsiblePreferences((prev: any) => ({
            ...prev,
            contactsPageMobileOpenedCollapsible: collapsible,
          }))
        );
      }
    },
  };
}
